import { useEffect, useMemo, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ApplicantSwitcher } from './ApplicantSwitcher';
import { BothApplicantsComposerCallout } from './BothApplicantsComposerCallout';
import { ChatHeader } from './ChatHeader';
import { ChatNumberBanner } from './ChatNumberBanner';
import { WindowBanner } from './WindowBanner';
import Spinner from '~/components/icons/Spinner';
import { MessageStream } from '~/features/whatsapp/components/MessageStream';
import { DEFAULT_MESSAGE_PAGE_SIZE } from '~/features/whatsapp/constants';
import { useChatMessages } from '~/features/whatsapp/hooks/useChatMessages';
import { useChats } from '~/features/whatsapp/hooks/useChats';
import { useChatSocket } from '~/features/whatsapp/hooks/useChatSocket';
import { useMarkChatViewedMutation } from '~/features/whatsapp/hooks/useMarkChatViewedMutation';
import { useOwnDivisionPhoneNumberId } from '~/features/whatsapp/hooks/useOwnDivisionPhoneNumberId';
import { closePanel } from '~/features/whatsapp/stores/chatPanelStore';
import type { LabeledMessage } from '~/features/whatsapp/types/message';
import { findLastInboundMessage } from '~/features/whatsapp/utils/findLastInboundMessage';
import type { ApplicantFilter } from '~/features/whatsapp/utils/resolveApplicantChats';
import { resolveCombinedNumberBanner } from '~/features/whatsapp/utils/resolveCombinedNumberBanner';
import { resolveSenderUnavailable } from '~/features/whatsapp/utils/resolveSenderUnavailable';

type Props = {
    kn1ChatId: string | null;
    kn2ChatId: string | null;
    kn1Label: string;
    kn2Label: string;
    loanRequestIds: Array<string>;
    activeApplicantFilter: ApplicantFilter;
    onApplicantFilterChange: (filter: ApplicantFilter) => void;
    headerDisplayName: string;
    resolved: boolean;
};

export const BothApplicantsChatPane = ({
    activeApplicantFilter,
    headerDisplayName,
    kn1ChatId,
    kn1Label,
    kn2ChatId,
    kn2Label,
    loanRequestIds,
    onApplicantFilterChange,
    resolved,
}: Props) => {
    const { t } = useTranslation();
    const { data: chats } = useChats();

    const kn1Chat = kn1ChatId ? chats?.find((entry) => entry.id === kn1ChatId) : undefined;
    const kn2Chat = kn2ChatId ? chats?.find((entry) => entry.id === kn2ChatId) : undefined;

    const sharedChat = kn1ChatId != null && kn1ChatId === kn2ChatId;

    const {
        fetchNextPage: fetchKn1NextPage,
        hasNextPage: kn1HasNextPage,
        isError: kn1Error,
        isFetchingNextPage: kn1FetchingNextPage,
        isLoading: kn1Loading,
        messages: kn1Messages,
    } = useChatMessages(kn1ChatId ?? '', DEFAULT_MESSAGE_PAGE_SIZE);
    const {
        fetchNextPage: fetchKn2NextPage,
        hasNextPage: kn2HasNextPage,
        isError: kn2Error,
        isFetchingNextPage: kn2FetchingNextPage,
        isLoading: kn2Loading,
        messages: kn2Messages,
    } = useChatMessages(sharedChat ? '' : kn2ChatId ?? '', DEFAULT_MESSAGE_PAGE_SIZE);

    const ownPhoneNumberId = useOwnDivisionPhoneNumberId();
    const numberBanner = resolveCombinedNumberBanner({
        candidates: [
            { chat: kn1Chat, senderUnavailable: resolveSenderUnavailable({ chat: kn1Chat, messages: kn1Messages }) },
            {
                chat: kn2Chat,
                senderUnavailable: resolveSenderUnavailable({ chat: kn2Chat, messages: sharedChat ? [] : kn2Messages }),
            },
        ],
        ownPhoneNumberId,
    });

    const markKn1Viewed = useMarkChatViewedMutation(kn1ChatId ?? '');
    const markKn2Viewed = useMarkChatViewedMutation(kn2ChatId ?? '');
    const lastMarkedKn1Ref = useRef<string | null>(null);
    const lastMarkedKn2Ref = useRef<string | null>(null);
    const kn1LastSequenceRef = useRef<number | null>(null);
    const kn2LastSequenceRef = useRef<number | null>(null);

    useEffect(() => {
        kn1LastSequenceRef.current = kn1Messages.at(-1)?.sequence ?? null;
    }, [kn1Messages]);

    useEffect(() => {
        kn2LastSequenceRef.current = kn2Messages.at(-1)?.sequence ?? null;
    }, [kn2Messages]);

    useChatSocket(kn1ChatId ?? undefined, {
        lastSequenceRef: kn1LastSequenceRef,
        onForbidden: () => {
            toast.error(t('WHATSAPP_CHAT.CHAT.ACCESS_REVOKED'));
            closePanel();
        },
    });
    useChatSocket(sharedChat ? undefined : kn2ChatId ?? undefined, {
        lastSequenceRef: kn2LastSequenceRef,
        onForbidden: () => {
            toast.error(t('WHATSAPP_CHAT.CHAT.ACCESS_REVOKED'));
            closePanel();
        },
    });

    useEffect(() => {
        if (!kn1ChatId) return;
        const lastInbound = findLastInboundMessage(kn1Messages);
        if (!lastInbound) return;
        const { messageId } = lastInbound;
        if (lastMarkedKn1Ref.current === messageId) return;
        markKn1Viewed.mutate(messageId, {
            onSuccess: () => {
                lastMarkedKn1Ref.current = messageId;
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kn1Messages, kn1ChatId]);

    useEffect(() => {
        if (!kn2ChatId) return;
        const lastInbound = findLastInboundMessage(kn2Messages);
        if (!lastInbound) return;
        const { messageId } = lastInbound;
        if (lastMarkedKn2Ref.current === messageId) return;
        markKn2Viewed.mutate(messageId, {
            onSuccess: () => {
                lastMarkedKn2Ref.current = messageId;
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kn2Messages, kn2ChatId]);

    const labeledMessages = useMemo((): Array<LabeledMessage> => {
        if (sharedChat) {
            return kn1Messages.map((message) => ({ message }));
        }
        const merged = [
            ...kn1Messages.map((message) => ({ message, senderLabel: kn1Label })),
            ...kn2Messages.map((message) => ({ message, senderLabel: kn2Label })),
        ];
        return merged.sort((left, right) => left.message.timestamp - right.message.timestamp);
    }, [kn1Label, kn1Messages, kn2Label, kn2Messages, sharedChat]);

    const combinedLastInboundAt = useMemo(() => {
        const timestamps = [kn1Chat?.lastInboundAt, kn2Chat?.lastInboundAt].filter(
            (value): value is number => value != null,
        );
        return timestamps.length > 0 ? Math.max(...timestamps) : null;
    }, [kn1Chat?.lastInboundAt, kn2Chat?.lastInboundAt]);

    const anchorChatId = kn1ChatId ?? kn2ChatId ?? '';
    const isLoading = (kn1ChatId ? kn1Loading : false) || (!sharedChat && kn2ChatId ? kn2Loading : false);
    const isError = (kn1ChatId ? kn1Error : false) || (!sharedChat && kn2ChatId ? kn2Error : false);
    const hasOlderMessages = Boolean(kn1HasNextPage) || (!sharedChat && Boolean(kn2HasNextPage));
    const isLoadingOlder = kn1FetchingNextPage || (!sharedChat && kn2FetchingNextPage);

    const loadOlderMessages = () => {
        if (kn1HasNextPage && !kn1FetchingNextPage) fetchKn1NextPage().catch(() => undefined);
        if (!sharedChat && kn2HasNextPage && !kn2FetchingNextPage) fetchKn2NextPage().catch(() => undefined);
    };

    return (
        <div className="flex h-full min-h-0 flex-col">
            <ChatHeader
                canLink={false}
                chatId={anchorChatId}
                displayName={headerDisplayName}
                lastInboundAt={combinedLastInboundAt}
                loanRequestIds={loanRequestIds}
                resolved={resolved}
            />
            <ApplicantSwitcher activeFilter={activeApplicantFilter} onChange={onApplicantFilterChange} />
            <WindowBanner lastInboundAt={combinedLastInboundAt} />
            {numberBanner && (
                <ChatNumberBanner chat={numberBanner.chat} senderUnavailable={numberBanner.senderUnavailable} />
            )}
            <div className="min-h-0 flex-1 overflow-y-auto">
                {isLoading && (
                    <div className="flex items-center justify-center py-10">
                        <Spinner />
                    </div>
                )}
                {isError && (
                    <div className="px-4 py-4 text-sm text-alert-error">
                        {t('WHATSAPP_CHAT.ERROR.LOAD_MESSAGES_FAILED')}
                    </div>
                )}
                {!isLoading && labeledMessages.length > 0 && (
                    <MessageStream
                        chatId={anchorChatId}
                        labeledMessages={labeledMessages}
                        hasOlderMessages={hasOlderMessages}
                        isLoadingOlder={isLoadingOlder}
                        onLoadOlder={loadOlderMessages}
                    />
                )}
            </div>
            <BothApplicantsComposerCallout />
        </div>
    );
};
